import { ComponentProps, useCallback } from 'react';
import ActivityCard from '@alltrails/cards/components/ActivityCard';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { AnonymizedActivity } from '@alltrails/types/Activity';
import { useAppSelector } from '../../redux';
import PaddingAwarePopup from '../PaddingAwarePopup/PaddingAwarePopup';
import PopupCarouselControls from '../PopupCarouselControls';
import styles from './CommunityActivityPopup.module.scss';

type CommunityActivityPopupProps = Pick<ComponentProps<typeof PaddingAwarePopup>, 'latitude' | 'longitude' | 'offset'> & {
  activities: AnonymizedActivity[];
  index: number;
  onIndexChange: (newIndex: number) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const CommunityActivityPopup = ({
  activities,
  index,
  latitude,
  longitude,
  offset,
  onIndexChange,
  onMouseEnter,
  onMouseLeave
}: CommunityActivityPopupProps) => {
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();
  const displayMetric = useAppSelector(state => state.context.displayMetric);

  const renderActivityCard = useCallback(
    (activity: AnonymizedActivity) => (
      <ActivityCard
        activity={{ ...activity, slug: activity.anonymizedSlug }}
        className={styles.mobileActivityCard}
        languageRegionCode={languageRegionCode}
        openInNewTab
        showAnonymizedUser
        useMetric={displayMetric}
        variant="small"
      />
    ),
    [displayMetric, languageRegionCode]
  );

  const activity = activities[index];
  if (!activity) {
    return null;
  }

  if (isMobile) {
    return (
      <div className={styles.mobilePopup}>
        <Carousel
          uniqueId="community-activity-carousel"
          items={activities}
          renderItem={renderActivityCard}
          getItemKey={activity => `community-activity-${activity.id}`}
          onSlideChange={onIndexChange}
          showPaginationDots={false}
          slidesPerViewMobile={1}
        />
      </div>
    );
  }

  return (
    <PaddingAwarePopup
      className={styles.popup}
      closeButton={false}
      closeOnClick={false}
      latitude={latitude}
      longitude={longitude}
      maxWidth="327"
      offset={offset}
    >
      <div className={styles.popupContents} onMouseEnter={() => onMouseEnter?.()} onMouseLeave={() => onMouseLeave?.()}>
        <ActivityCard
          activity={{ ...activity, slug: activity.anonymizedSlug }}
          languageRegionCode={languageRegionCode}
          openInNewTab
          showActivityType
          showAnonymizedUser
          useMetric={displayMetric}
          variant="small"
        />
        {activities.length > 1 && (
          <PopupCarouselControls className={styles.popupControls} index={index} onIndexChange={onIndexChange} total={activities.length} />
        )}
      </div>
    </PaddingAwarePopup>
  );
};

export default CommunityActivityPopup;
